import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';

export const invoiceTemplatesKey = ['invoiceTemplates'] as const;

/**
 * Die Vorlagenliste samt ihrer beiden Listenaktionen.
 *
 * <p>Ein Workspace hat genau eine Standardvorlage — sie wird gezogen, wenn am Apartment nichts
 * anderes hinterlegt ist. Im Datenmodell heißt sie `isPrimary`; die Oberfläche sagt seit Block C
 * durchgehend „Standardvorlage des Workspace", damit dieselbe Sache nicht an zwei Stellen
 * verschieden heißt.</p>
 */
export function useInvoiceTemplates() {
  const api = useApi();
  const qc = useQueryClient();

  const showError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.detail ?? e.title);
  };

  const query = useQuery({
    queryKey: invoiceTemplatesKey,
    queryFn: () => api.invoiceTemplates.list(),
  });

  const setPrimary = useMutation({
    mutationFn: (id: string) => api.invoiceTemplates.setPrimary(id),
    onSuccess: () => {
      toast.success('Standardvorlage gewechselt');
      void qc.invalidateQueries({ queryKey: invoiceTemplatesKey });
    },
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.invoiceTemplates.delete(id),
    onSuccess: () => {
      toast.success('Rechnungsvorlage gelöscht');
      void qc.invalidateQueries({ queryKey: invoiceTemplatesKey });
    },
    onError: showError,
  });

  return {
    templates: query.data,
    isLoading: query.isLoading,
    hasError: query.isError,
    setPrimary,
    remove,
    invalidate: () => void qc.invalidateQueries({ queryKey: invoiceTemplatesKey }),
  };
}
